import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Badge,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    getNotifications,
    markNotificationsRead,
} from '../../actions/notification';
import { notificationDestination } from '../../utils/notificationDeepLink';
import NotificationFrequencySettings from './NotificationFrequencySettings';

dayjs.extend(relativeTime);

const HAT_HINT = {
    HOST: 'Host',
    ARTIST: 'Artist',
    ATTENDEE: 'Attendee',
};

const NotificationsBell = ({
    auth,
    notification,
    getNotifications,
    markNotificationsRead,
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const isAuthenticated = auth && auth.isAuthenticated;
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (isAuthenticated) {
            getNotifications();
        }
    }, [isAuthenticated, getNotifications]);

    const items = notification && notification.notifications
        ? notification.notifications
        : [];
    const unreadCount =
        notification && typeof notification.unreadCount === 'number'
            ? notification.unreadCount
            : 0;

    const unreadLabel = useMemo(() => {
        if (unreadCount > 9) return '9+';
        return unreadCount;
    }, [unreadCount]);

    const hatChannels = useMemo(() => {
        const roles =
            auth && auth.user && Array.isArray(auth.user.role)
                ? auth.user.role
                : [];
        const labels = [];
        if (roles.includes('HOST')) labels.push('hosting');
        if (roles.includes('ARTIST')) labels.push('booking');
        if (roles.includes('ATTENDEE')) labels.push('attending');
        return labels;
    }, [auth]);

    const hatScheduleSentence = useMemo(() => {
        if (hatChannels.length < 2) return '';
        const list =
            hatChannels.length === 2
                ? `${hatChannels[0]} and ${hatChannels[1]}`
                : `${hatChannels.slice(0, -1).join(', ')}, and ${
                      hatChannels[hatChannels.length - 1]
                  }`;
        return ` Each hat has its own schedule so ${list} emails don’t delay each other.`;
    }, [hatChannels]);

    if (!isAuthenticated) return null;

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        getNotifications();
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSettingsOpen(false);
    };

    const handleMarkReadDot = (event, item) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!item.readAt) {
            markNotificationsRead([item._id]);
        }
    };

    const handleClickItem = (item) => {
        if (!item.readAt) {
            markNotificationsRead([item._id]);
        }
        handleClose();
        const dest = notificationDestination(item);
        if (dest) {
            navigate(dest, {
                state: { fromNotification: item._id, openedAt: Date.now() },
            });
        }
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleOpen}
                    color="inherit"
                    size="large"
                    aria-label="Notifications"
                    sx={{ mr: 1 }}
                >
                    <Badge
                        badgeContent={unreadLabel}
                        color="error"
                        overlap="circular"
                        invisible={unreadCount < 1}
                    >
                        <NotificationsNoneOutlinedIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                className="porchlightBG"
                PaperProps={{
                    sx: {
                        width: settingsOpen ? 640 : 'max-content',
                        minWidth: settingsOpen ? 640 : 360,
                        maxWidth: 'calc(100vw - 24px)',
                        maxHeight: '70vh',
                        mt: 1,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 12px 8px 8px',
                    }}
                >
                    {settingsOpen ? (
                        <IconButton
                            size="small"
                            aria-label="Back to notifications"
                            onClick={() => setSettingsOpen(false)}
                            sx={{ color: 'var(--link-color)', mr: 0.5 }}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 34 }} />
                    )}
                    <Typography
                        component="h2"
                        sx={{ fontSize: '1.15rem', flexGrow: 1 }}
                    >
                        {settingsOpen
                            ? 'Notification settings'
                            : 'Notifications'}
                    </Typography>
                    {!settingsOpen && (
                        <Tooltip title="Notification settings">
                            <IconButton
                                size="small"
                                aria-label="Notification settings"
                                onClick={() => setSettingsOpen(true)}
                                sx={{ color: 'var(--link-color)' }}
                            >
                                <SettingsOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {settingsOpen ? (
                    <Box sx={{ padding: '8px 16px 20px' }}>
                        <Typography
                            component="p"
                            sx={{
                                fontSize: '.85em',
                                marginBottom: '8px',
                                color: 'var(--primary-color)',
                            }}
                        >
                            These cadences only affect batched email. New items
                            still show up here right away.
                            {hatScheduleSentence}
                        </Typography>
                        <NotificationFrequencySettings />
                    </Box>
                ) : items.length === 0 ? (
                    <Typography
                        sx={{
                            padding: '24px 16px 32px',
                            textAlign: 'center',
                            opacity: 0.7,
                        }}
                    >
                        You’re all caught up.
                    </Typography>
                ) : (
                    <List
                        sx={{
                            paddingTop: 0,
                            overflowY: 'auto',
                            width: 'max-content',
                            minWidth: '100%',
                            maxWidth: '100%',
                        }}
                    >
                        {items.map((item) => (
                            <ListItem
                                key={item._id}
                                disablePadding
                                secondaryAction={
                                    !item.readAt ? (
                                        <Tooltip title="Mark as read">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                aria-label="Mark as read"
                                                onClick={(event) =>
                                                    handleMarkReadDot(
                                                        event,
                                                        item
                                                    )
                                                }
                                                sx={{
                                                    marginTop: '6px',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            'var(--link-color)',
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null
                                }
                                sx={{
                                    alignItems: 'flex-start',
                                    width: 'max-content',
                                    minWidth: '100%',
                                    maxWidth: '100%',
                                    borderTop:
                                        '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <ListItemButton
                                    onClick={() => handleClickItem(item)}
                                    sx={{
                                        alignItems: 'flex-start',
                                        width: 'max-content',
                                        minWidth: '100%',
                                        maxWidth: '100%',
                                        paddingRight: item.readAt
                                            ? '16px'
                                            : '48px',
                                    }}
                                >
                                    <ListItemText
                                        primary={item.title}
                                        secondary={
                                            <>
                                                {item.body
                                                    ? `${item.body} · `
                                                    : ''}
                                                {HAT_HINT[item.hat] ||
                                                    item.hat}
                                                {' · '}
                                                {item.createdAt
                                                    ? dayjs(
                                                          item.createdAt
                                                      ).fromNow()
                                                    : ''}
                                            </>
                                        }
                                        sx={{
                                            flex: '0 1 auto',
                                            minWidth: 0,
                                        }}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontFamily:
                                                    'var(--primary-font)',
                                                color: item.readAt
                                                    ? 'inherit'
                                                    : 'var(--link-color)',
                                                width: 'max-content',
                                                maxWidth: '100%',
                                            },
                                        }}
                                        secondaryTypographyProps={{
                                            sx: { fontSize: '0.75rem' },
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    );
};

NotificationsBell.propTypes = {
    auth: PropTypes.object.isRequired,
    notification: PropTypes.object,
    getNotifications: PropTypes.func.isRequired,
    markNotificationsRead: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
    notification: state.notification,
});

export default connect(mapStateToProps, {
    getNotifications,
    markNotificationsRead,
})(NotificationsBell);
